import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { OrderStatus, SellerOrderListResponse, SmartBucketId } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

// Payment method values accepted by the backend controller
export type OrdersQueryPaymentMethod = "COD" | "EXTERNAL_ONLINE";
// Sort order accepted by the backend
export type OrdersQuerySort = "newest" | "oldest";

type UseSellerOrdersQueryParams = {
  storeSlug: string;
  limit?: number;
  offset?: number;
  /** Status filter — array of OrderStatus values sent as repeated ?status= params */
  status?: OrderStatus[];
  /** Legacy typed-search — kept for backward compat. Use `q` for new calls. */
  search?: string;
  searchType?: "all" | "orderCode";
  /** Free-text multi-field search (order code, buyer code, customer name, phone).
   *  Supersedes `search` when present. Debounced in the caller. */
  q?: string;
  /** Governorate UUIDs (Phase 4 UI; param sent now so Phase 4 only adds UI) */
  governorateIds?: string[];
  /** ISO date string for the start of the date range (inclusive) */
  dateFrom?: string;
  /** ISO date string for the end of the date range (inclusive) */
  dateTo?: string;
  /** Payment method filter — must match backend enum exactly */
  paymentMethod?: OrdersQueryPaymentMethod;
  /** Shipment status (Phase 4 UI; param sent now so Phase 4 only adds UI) */
  shipmentStatus?: string;
  /** Sort order */
  sort?: OrdersQuerySort;
  /** Smart bucket filter — when set, the backend uses keyset pagination mode.
   * Overrides `status` on the backend. */
  bucket?: SmartBucketId;
  /** Opaque keyset cursor from a previous `nextCursor` response field.
   * The FE must not parse this value. */
  cursor?: string;
};

type NormalizedFilters = {
  limit?: number;
  offset?: number;
  status?: OrderStatus[];
  search?: string;
  searchType?: "orderCode";
  q?: string;
  governorateIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: OrdersQueryPaymentMethod;
  shipmentStatus?: string;
  sort?: OrdersQuerySort;
  bucket?: SmartBucketId;
  cursor?: string;
};

type QueryKeyFilters = {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
  searchType?: "orderCode";
  q?: string;
  governorateIds?: string;
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: string;
  shipmentStatus?: string;
  sort?: string;
  bucket?: string;
  cursor?: string;
};

function normalizeFilters({
  limit,
  offset,
  status,
  search,
  searchType,
  q,
  governorateIds,
  dateFrom,
  dateTo,
  paymentMethod,
  shipmentStatus,
  sort,
  bucket,
  cursor,
}: Omit<UseSellerOrdersQueryParams, "storeSlug">): NormalizedFilters {
  const normalizedSearch = search?.trim();
  const normalizedQ = q?.trim();
  const normalizedStatus = status
    ? Array.from(new Set(status.map((item) => item.trim()).filter((item) => item.length > 0)))
        .sort()
        .map((item) => item as OrderStatus)
    : undefined;
  const normalizedGovIds =
    governorateIds && governorateIds.length > 0
      ? governorateIds.map((id) => id.trim()).filter((id) => id.length > 0)
      : undefined;

  return {
    limit,
    offset,
    status: normalizedStatus && normalizedStatus.length > 0 ? normalizedStatus : undefined,
    search: normalizedSearch && normalizedSearch.length > 0 ? normalizedSearch : undefined,
    searchType: searchType && searchType !== "all" ? searchType : undefined,
    q: normalizedQ && normalizedQ.length > 0 ? normalizedQ : undefined,
    governorateIds: normalizedGovIds && normalizedGovIds.length > 0 ? normalizedGovIds : undefined,
    dateFrom: dateFrom ?? undefined,
    dateTo: dateTo ?? undefined,
    paymentMethod: paymentMethod ?? undefined,
    shipmentStatus: shipmentStatus ?? undefined,
    sort: sort ?? undefined,
    bucket: bucket ?? undefined,
    cursor: cursor ?? undefined,
  };
}

function buildPath(filters: NormalizedFilters) {
  const query = new URLSearchParams();
  if (filters.limit !== undefined) {
    query.set("limit", String(filters.limit));
  }
  if (filters.offset !== undefined) {
    query.set("offset", String(filters.offset));
  }
  if (filters.status) {
    for (const status of filters.status) {
      query.append("status", status);
    }
  }
  if (filters.search) {
    query.set("search", filters.search);
  }
  if (filters.searchType) {
    query.set("searchType", filters.searchType);
  }
  if (filters.q) {
    query.set("q", filters.q);
  }
  if (filters.governorateIds) {
    query.set("governorateIds", filters.governorateIds.join(","));
  }
  if (filters.dateFrom) {
    query.set("dateFrom", filters.dateFrom);
  }
  if (filters.dateTo) {
    query.set("dateTo", filters.dateTo);
  }
  if (filters.paymentMethod) {
    query.set("paymentMethod", filters.paymentMethod);
  }
  if (filters.shipmentStatus) {
    query.set("shipmentStatus", filters.shipmentStatus);
  }
  if (filters.sort) {
    query.set("sort", filters.sort);
  }
  if (filters.bucket) {
    query.set("bucket", filters.bucket);
  }
  if (filters.cursor) {
    query.set("cursor", filters.cursor);
  }

  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.seller.orders}?${queryString}`
    : endpoints.seller.orders;
}

function toQueryKeyFilters(filters: NormalizedFilters): QueryKeyFilters {
  return {
    limit: filters.limit,
    offset: filters.offset,
    status: filters.status?.join(","),
    search: filters.search,
    searchType: filters.searchType,
    q: filters.q,
    governorateIds: filters.governorateIds?.join(","),
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    paymentMethod: filters.paymentMethod,
    shipmentStatus: filters.shipmentStatus,
    sort: filters.sort,
    bucket: filters.bucket,
    cursor: filters.cursor,
  };
}

export function useOrdersQuery(params: UseSellerOrdersQueryParams) {
  const { storeSlug, ...filters } = params;
  const normalizedFilters = normalizeFilters(filters);
  const queryKeyFilters = toQueryKeyFilters(normalizedFilters);

  const query = useQuery({
    queryKey: queryKeys.seller.orders(storeSlug, queryKeyFilters),
    queryFn: () => apiJsonClient<SellerOrderListResponse>(buildPath(normalizedFilters)),
    enabled: storeSlug.length > 0,
    // Keep the current page visible while a new one (e.g. a larger `limit` for
    // "load more", or a changed filter) is fetching — avoids an empty flash.
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    /** True during any fetch, including background refetches / param changes. */
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    /** Timestamp (ms) of the last successful fetch; 0 before the first one. */
    dataUpdatedAt: query.dataUpdatedAt,
    /** Opaque keyset cursor for the next page; present only on bucket-paginated
     * responses with more rows following. Pass as `cursor` on the next call. */
    nextCursor: query.data?.nextCursor,
  };
}
